import CustomLinkSanitizer from './CustomLinkSanitizer';
import { nestQuillListHtml } from './nestQuillListHtml';
import registerQuillModules from './registerQuillModules';
import useQuillEditorModules from './useQuillEditorModules';
import { useQuillEditorSxProps } from './useQuillEditorStyles';
import { BaseProps } from '#components';
import { ErrorBoundary } from '#helper-components';
import { Box, SxProps, Theme, Typography } from '@mui/material';
import { useTranslation } from '@modernloop/i18n';
import type { FC } from 'react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import { useDebouncedCallback } from 'use-debounce';

type Ref = React.MutableRefObject<ReactQuill | null>;
export type Props = BaseProps &
  Omit<React.ComponentProps<typeof ReactQuill>, 'value' | 'defaultValue'> & {
    quillRef?: Ref;
    value?: string;
    defaultValue?: string;
    editorHeight?: number;
    debounceTimeout?: number;
    autoFocus?: boolean;
    sx?: SxProps<Theme>;
  };

// Timeout interval to wait when a user is typing fast and prevent updating the components to frequently.
export const DEFAULT_DEBOUNCE_TIMEOUT_MS = 250;

// Link tooltip sometimes renders outside of the editor
// Github - https://github.com/quilljs/quill/issues/764
// Quill docs - https://quilljs.com/docs/configuration/#bounds
const QUILL_EDITOR_BOUNDS_SELECTOR = 'quill-editor-paper-container';

// Need to register all the quill modules here to get correct behavior
registerQuillModules();

// See issue here: https://github.com/quilljs/parchment/issues/87
// if a <br> gets in the beginning or end of a node ex `<em><br>foobar</em>` quill will fail
const makeValueSafe = (value: string | null): string => {
  if (!value) {
    return '';
  } // must return a string
  return (
    value
      // turn <div><br>Text<div> to <div>&nbsp;<br>Text<div> unless it's <div><br></div>
      .replaceAll(/><br>/g, '>&nbsp;<br>')
      .replaceAll(/>&nbsp;<br></g, '><br><')
      // turn <div>Text<br><div> to <div>Text<br>&nbsp;<div> unless it's <div><br></div>
      .replaceAll(/<br></g, '<br>&nbsp;<')
      .replaceAll(/><br>&nbsp;</g, '><br><')
  );
};

// We are using <div> going forward to text blocks
// <div>s are single spaced, <p> are double.
// We need to add a new line when converting from <p>
const convertFromPTagToDivTag = (value: string | null): string => {
  if (!value) {
    return '';
  } // must return a string
  return value
    .replaceAll(/<\/p>/g, '</div><div><br></div>') // end tags, add a newline
    .replaceAll(/<p>/g, '<div>'); // start tags
};

// export const useReactQuillRef = (): Ref | undefined => {
//   return useRef<ReactQuill | null>(null);
// };

const QuillEditor: FC<Props> = ({
  dataTestId,
  sx,
  value,
  defaultValue,
  editorHeight,
  quillRef,
  autoFocus,
  debounceTimeout = DEFAULT_DEBOUNCE_TIMEOUT_MS,
  onChange,
  modules: propModules,
  ...rest
}) => {
  const { t } = useTranslation();
  const sxProps = useQuillEditorSxProps({ height: editorHeight });
  const modules = useQuillEditorModules(quillRef);
  const newValue = makeValueSafe(convertFromPTagToDivTag(value || ''));
  const newDefaultValue =
    defaultValue === undefined ? undefined : makeValueSafe(convertFromPTagToDivTag(defaultValue || ''));
  const [keyRef, setKeyRef] = useState<number>(0);
  const contentRef = useRef<string | undefined>(newValue);
  const fallback = <Typography variant="caption">{t('common:shared.editor.loadError')}</Typography>;

  // Since we are using ReactQuill in "uncontrolled" mode by passing defaultValue, we have this
  // useEffect in cases where we need to reset the content (likely due to placeholder token values being filled)
  // It does this by updating the key which causes ReactQuill to do a full remount, updating it's content to
  // the value passed in.
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes local state from external inputs; prefer derived state when possible.
  useEffect(() => {
    // if the `value` prop is same as the string value passed to onChange then we can skip re-render.
    if (value === contentRef.current) {
      return;
    }

    // Otherwise, set the current content to the passed in value and update the keyRef to cause a re-render
    // Setting currentContent here prevents maxDepth exceeded
    contentRef.current = value;
    setKeyRef(keyRef + 1);
  }, [value, keyRef]);

  const ref = useRef<ReactQuill | null>(null);

  const activeRef = quillRef || ref;

  // Set focus on the editor when autoFocus is true
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect owns timer lifecycle after render and cleanup.
  useEffect(() => {
    if (autoFocus) {
      setTimeout(() => {
        if (activeRef.current) {
          activeRef.current.focus();

          // move cursor to the end of the editor
          const editor = activeRef.current?.getEditor();
          if (editor) {
            editor.setSelection(editor.getLength(), 0);
          }
        }
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (...args: Parameters<Exclude<typeof onChange, undefined>>) => {
    if (!onChange) {
      return;
    }

    // Quill emits flat lists with margin-left per item. Hand the app nested lists instead, so
    // emails, calendar invites, and pasted copies keep the sub-levels. The ref stores the nested
    // form because the parent passes that same string back as `value`.
    const [content, ...changeArgs] = args;
    const nestedContent = nestQuillListHtml(content);
    contentRef.current = nestedContent;
    onChange(nestedContent, ...changeArgs);
  };

  const debouncedOnChange = useDebouncedCallback(handleChange, debounceTimeout || DEFAULT_DEBOUNCE_TIMEOUT_MS);

  const id = useMemo(() => {
    // Need to generate unique id for bounds selector to account for multiple Editors on page.
    return `${QUILL_EDITOR_BOUNDS_SELECTOR}-${Date.now()}`;
  }, []);

  return (
    <ErrorBoundary
      scope="SharedQuillEditor"
      getContext={() => ({ text: value?.substring(0, 500) || '<EMPTY>' })}
      fallback={fallback}
    >
      <Box
        // The memoized style combines the editor base styles with caller overrides.
        // eslint-disable-next-line no-restricted-syntax
        sx={useMemo(() => ({ ...sxProps.root, ...sx }), [sx, sxProps.root])}
        color="default"
        data-testid={dataTestId}
        id={id}
      >
        <ReactQuill
          {...rest}
          key={keyRef}
          defaultValue={newDefaultValue ?? newValue ?? undefined}
          ref={activeRef}
          modules={propModules ?? modules}
          theme="snow"
          onChange={debounceTimeout ? debouncedOnChange : handleChange}
          bounds={`#${id}`}
        />
      </Box>
    </ErrorBoundary>
  );
};

/** * Code Patch start ** */
// https://github.com/quilljs/quill/issues/2096

Quill.register(CustomLinkSanitizer);

const Inline = Quill.import('blots/inline');
class CustomColor extends Inline {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(domNode: any, value: any) {
    super(domNode, value);

    // Map <font> properties
    domNode.style.color = domNode.color;

    const span = this.replaceWith(new Inline(Inline.create()));

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    span.children.forEach((child: any) => {
      if (child.attributes) {
        child.attributes.copy(span);
      }
      if (child.unwrap) {
        child.unwrap();
      }
    });

    this.remove();

    return span;
  }
}

CustomColor.blotName = 'customColor';
CustomColor.tagName = 'FONT';

Quill.register(CustomColor, true);
/** * Code Patch end ** */

QuillEditor.displayName = 'QuillEditor';
export default QuillEditor;
